import React, { useState, useEffect } from 'react';
import { SlimTeam, Team } from '../types/Team';
import { fetchTeams, fetchTeamById, updateTeam, createTeam } from '../api/apis';
import { TeamMetadata } from './TeamMetadata';

export const TeamManagement = () => {
  const [teams, setTeams] = useState<SlimTeam[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const loadTeams = async () => {
      setIsLoading(true);
      try {
        const response = await fetchTeams(searchQuery);
        setTeams(response);
        setError(null);
      } catch (err) {
        setError('Failed to load teams');
        console.error('Error loading teams:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(loadTeams, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleTeamSelect = async (team: SlimTeam) => {
    try {
      const fullTeam = await fetchTeamById(team.id);
      setSelectedTeam(fullTeam);
      setIsCreating(false);
    } catch (err) {
      console.error('Error loading team:', err);
      setError('Failed to load team details');
    }
  };

  const handleTeamUpdate = async (updatedTeam: Team) => {
    try {
      if (isCreating) {
        await createTeam(
          updatedTeam.name,
          updatedTeam.category,
          updatedTeam.members.map(m => m.name)
        );
      } else {
        await updateTeam(
          updatedTeam.id,
          updatedTeam.name,
          updatedTeam.category,
          updatedTeam.members.map(m => m.name)
        );
      }
      // Refresh the teams list
      const results = await fetchTeams(searchQuery);
      setTeams(results);
      setSelectedTeam(null);
      setIsCreating(false);
    } catch (err) {
      console.error('Error saving team:', err);
      setError(isCreating ? 'Failed to create team' : 'Failed to update team');
    }
  };

  const handleTeamDelete = async () => {
    try {
      // Refresh the teams list
      const results = await fetchTeams(searchQuery);
      setTeams(results);
      setSelectedTeam(null);
      setIsCreating(false);
    } catch (err) {
      console.error('Error refreshing teams:', err);
      setError('Failed to refresh teams list');
    }
  };

  const handleCreateTeam = () => {
    const newTeam: Team = {
      id: '',
      name: '',
      category: 'Mixed',
      members: [{ name: '' }],
      raceTime: {
        startTime: ''
      },
      startHeat: '',
      stationPauses: [],
      netRaceTime: { hours: 0, minutes: 0, seconds: 0 },
      totalPauseTime: { hours: 0, minutes: 0, seconds: 0 }
    };
    setSelectedTeam(newTeam);
    setIsCreating(true);
  };

  if (selectedTeam) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => {
            setSelectedTeam(null);
            setIsCreating(false);
          }}
          className="mb-6 flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Team List
        </button>
        <TeamMetadata 
          team={selectedTeam} 
          onUpdate={handleTeamUpdate}
          onDelete={handleTeamDelete}
          editable={true}
          isCreating={isCreating}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Team Management</h1>
        <button
          onClick={handleCreateTeam}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Create Team
        </button>
      </div>
      
      {/* Search Bar */}
      <div className="mb-8">
        <input
          type="text"
          placeholder="Search teams..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-red-600 mb-4">{error}</div>
      )}

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((team) => (
          <div
            key={team.id}
            onClick={() => handleTeamSelect(team)}
            className="bg-blue-500 text-white p-6 rounded-lg shadow-md hover:bg-blue-600 transition-colors cursor-pointer"
          >
            <h3 className="text-xl font-semibold mb-2">{team.name}</h3>
            <p className="text-blue-100 mb-3">Category: {team.category}</p>
            <div className="text-sm">
              <p className="font-medium mb-1">Team Members:</p>
              <ul className="list-disc list-inside">
                {team.members.map((member, index) => (
                  <li key={index} className="text-blue-100">{member}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* No Results Message */}
      {!isLoading && teams.length === 0 && (
        <div className="text-gray-600 text-center mt-8">
          No teams found matching your search.
        </div>
      )}
    </div>
  );
}; 